/**
 * Permission-driven rendering.
 *
 * **This is not a security control.** Every action is authorised on the server, and a
 * user who edits their session in a debugger gets a 403 rather than a merge. What this
 * does is stop the interface offering an action the person cannot take — which matters
 * because an analyst repeatedly clicking "Approve" and receiving 403 learns to ignore
 * errors, and that is a worse outcome than the button not being there.
 */

import { useSession } from "@/features/auth/session";

/** Every action the role matrix defines. */
export type Action =
  | "manage_tenant_settings"
  | "manage_users_and_roles"
  | "manage_credentials"
  | "manage_plugins"
  | "create_run_cases"
  | "delete_cases"
  | "view_cases_and_evidence"
  | "approve_merges"
  | "confirm_hypotheses"
  | "generate_reports"
  | "approve_export_reports"
  | "execute_erasure"
  | "read_audit_log";

/**
 * Whether the signed-in principal holds an action.
 *
 * @param action - The action.
 * @returns Whether the interface should offer it.
 */
export function useCan(action: Action): boolean {
  const session = useSession();
  return session?.permissions?.includes(action) ?? false;
}
